import { createContext, use, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react"

export type Theme = "light" | "dark" | "system"

const STORAGE_KEY = "alpaca-theme"

type ThemeState = {
  theme: Theme
  resolved: "light" | "dark"
  setTheme: (t: Theme) => void
}

const ThemeContext = createContext<ThemeState | null>(null)

const mediaQuery = () => window.matchMedia("(prefers-color-scheme: dark)")

/**
 * El tema del sistema es un **estado externo a React**, así que se lee con
 * `useSyncExternalStore` en vez de copiarlo a `useState` desde un efecto.
 *
 * La diferencia importa: con `setState` dentro de un efecto, cada cambio provoca un
 * render extra en cascada y el valor va un frame por detrás. Aquí React lee la
 * fuente directamente y se resuscribe solo.
 */
function useSistemaOscuro(): boolean {
  return useSyncExternalStore(
    (alCambiar) => {
      const mq = mediaQuery()
      mq.addEventListener("change", alCambiar)
      return () => mq.removeEventListener("change", alCambiar)
    },
    () => mediaQuery().matches,
    () => false,
  )
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? "system",
  )
  const sistemaOscuro = useSistemaOscuro()

  // Derivado durante el render, no guardado en estado: dos fuentes para el mismo
  // dato es como se acaba pintando un tema y aplicando otro.
  const resolved: "light" | "dark" =
    theme === "system" ? (sistemaOscuro ? "dark" : "light") : theme

  // Esto sí es un efecto legítimo: sincroniza React con el DOM, que es un sistema
  // externo. El destello inicial ya lo evita el script inline de index.html.
  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle("dark", resolved === "dark")
    root.style.colorScheme = resolved
  }, [resolved])

  const setTheme = useCallback((t: Theme) => {
    localStorage.setItem(STORAGE_KEY, t)
    setThemeState(t)
  }, [])

  const value = useMemo(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme])

  return <ThemeContext value={value}>{children}</ThemeContext>
}

export function useTheme(): ThemeState {
  const ctx = use(ThemeContext)
  if (ctx === null) throw new Error("useTheme fuera de ThemeProvider")
  return ctx
}
